import { schema } from 'normalizr';
import { RSAA, createAction } from 'redux-api-middleware';
import { handleResponse, handleError, buildQueryString } from '../utils';
import { calculateLineItemStatus, getCalculatedAdjustmentStatus } from '../utils/adjustmentUtils';

export const DELETE_ADJUSTMENT_REQUEST = 'DELETE_ADJUSTMENT_REQUEST';
export const DELETE_ADJUSTMENT_SUCCESS = 'DELETE_ADJUSTMENT_SUCCESS';
export const DELETE_ADJUSTMENT_FAILURE = 'DELETE_ADJUSTMENT_FAILURE';

export const FETCH_ADJUSTMENTDETAILS_REQUEST = 'FETCH_ADJUSTMENTDETAILS_REQUEST';
export const FETCH_ADJUSTMENTDETAILS_SUCCESS = 'FETCH_ADJUSTMENTDETAILS_SUCCESS';
export const FETCH_ADJUSTMENTDETAILS_FAILURE = 'FETCH_ADJUSTMENTDETAILS_FAILURE';
export const FETCH_ADJUSTMENTS_REQUEST = 'FETCH_ADJUSTMENTS_REQUEST';
export const FETCH_ADJUSTMENTS_SUCCESS = 'FETCH_ADJUSTMENTS_SUCCESS';
export const FETCH_ADJUSTMENTS_FAILURE = 'FETCH_ADJUSTMENTS_FAILURE';

export const FETCH_ADJUSTMENTSFORREVIEW_REQUEST = 'FETCH_ADJUSTMENTSFORREVIEW_REQUEST';
export const FETCH_ADJUSTMENTSFORREVIEW_SUCCESS = 'FETCH_ADJUSTMENTSFORREVIEW_SUCCESS';
export const FETCH_ADJUSTMENTSFORREVIEW_FAILURE = 'FETCH_ADJUSTMENTSFORREVIEW_FAILURE';

export const UPLOAD_ADJUSTMENT_ATTACHMENT_REQUEST = 'UPLOAD_ADJUSTMENT_ATTACHMENT_REQUEST';
export const UPLOAD_ADJUSTMENT_ATTACHMENT_SUCCESS = 'UPLOAD_ADJUSTMENT_ATTACHMENT_SUCCESS';
export const UPLOAD_ADJUSTMENT_ATTACHMENT_FAILURE = 'UPLOAD_ADJUSTMENT_ATTACHMENT_FAILURE';

export const CREATE_ADJUSTMENT_REQUEST = 'CREATE_ADJUSTMENT_REQUEST';
export const CREATE_ADJUSTMENT_SUCCESS = 'CREATE_ADJUSTMENT_SUCCESS';
export const CREATE_ADJUSTMENT_FAILURE = 'CREATE_ADJUSTMENT_FAILURE';
export const EDIT_ADJUSTMENT_REQUEST = 'EDIT_ADJUSTMENT_REQUEST';
export const EDIT_ADJUSTMENT_SUCCESS = 'EDIT_ADJUSTMENT_SUCCESS';
export const EDIT_ADJUSTMENT_FAILURE = 'EDIT_ADJUSTMENT_FAILURE';
export const SUBMIT_ADJUSTMENT_REQUEST = 'SUBMIT_ADJUSTMENT_REQUEST';
export const SUBMIT_ADJUSTMENT_SUCCESS = 'SUBMIT_ADJUSTMENT_SUCCESS';
export const SUBMIT_ADJUSTMENT_FAILURE = 'SUBMIT_ADJUSTMENT_FAILURE';
export const SUBMIT_EXISTING_ADJUSTMENT_REQUEST = 'SUBMIT_EXISTING_ADJUSTMENT_REQUEST';
export const SUBMIT_EXISTING_ADJUSTMENT_SUCCESS = 'SUBMIT_EXISTING_ADJUSTMENT_SUCCESS';
export const SUBMIT_EXISTING_ADJUSTMENT_FAILURE = 'SUBMIT_EXISTING_ADJUSTMENT_FAILURE';

export const APPROVE_ADJUSTMENT_REQUEST = 'APPROVE_ADJUSTMENT_REQUEST';
export const APPROVE_ADJUSTMENT_SUCCESS = 'APPROVE_ADJUSTMENT_SUCCESS';
export const APPROVE_ADJUSTMENT_FAILURE = 'APPROVE_ADJUSTMENT_FAILURE';

export const REJECT_ADJUSTMENT_REQUEST = 'REJECT_ADJUSTMENT_REQUEST';
export const REJECT_ADJUSTMENT_SUCCESS = 'REJECT_ADJUSTMENT_SUCCESS';
export const REJECT_ADJUSTMENT_FAILURE = 'REJECT_ADJUSTMENT_FAILURE';

export const REVERSE_ADJUSTMENT_DRAFT_REQUEST = 'REVERSE_ADJUSTMENT_DRAFT_REQUEST';
export const REVERSE_ADJUSTMENT_DRAFT_SUCCESS = 'REVERSE_ADJUSTMENT_DRAFT_SUCCESS';
export const REVERSE_ADJUSTMENT_DRAFT_FAILURE = 'REVERSE_ADJUSTMENT_DRAFT_FAILURE';

export const REVERSE_ADJUSTMENT_SUBMIT_REQUEST = 'REVERSE_ADJUSTMENT_SUBMIT_REQUEST';
export const REVERSE_ADJUSTMENT_SUBMIT_SUCCESS = 'REVERSE_ADJUSTMENT_SUBMIT_SUCCESS';
export const REVERSE_ADJUSTMENT_SUBMIT_FAILURE = 'REVERSE_ADJUSTMENT_SUBMIT_FAILURE';

export const REVERSE_ADJUSTMENT_APPROVE_REQUEST = 'REVERSE_ADJUSTMENT_APPROVE_REQUEST';
export const REVERSE_ADJUSTMENT_APPROVE_SUCCESS = 'REVERSE_ADJUSTMENT_APPROVE_SUCCESS';
export const REVERSE_ADJUSTMENT_APPROVE_FAILURE = 'REVERSE_ADJUSTMENT_APPROVE_FAILURE';

export const REVERSE_ADJUSTMENT_REJECT_REQUEST = 'REVERSE_ADJUSTMENT_REJECT_REQUEST';
export const REVERSE_ADJUSTMENT_REJECT_SUCCESS = 'REVERSE_ADJUSTMENT_REJECT_SUCCESS';
export const REVERSE_ADJUSTMENT_REJECT_FAILURE = 'REVERSE_ADJUSTMENT_REJECT_FAILURE';

export const FETCH_ADJUSTMENTACTIVITY_REQUEST = 'FETCH_ADJUSTMENTACTIVITY_REQUEST';
export const FETCH_ADJUSTMENTACTIVITY_SUCCESS = 'FETCH_ADJUSTMENTACTIVITY_SUCCESS';
export const FETCH_ADJUSTMENTACTIVITY_FAILURE = 'FETCH_ADJUSTMENTACTIVITY_FAILURE';

export const DISCARD_ADJUSTMENT_REVERSAL_REQUEST = 'DISCARD_ADJUSTMENT_REVERSAL_REQUEST';
export const DISCARD_ADJUSTMENT_REVERSAL_SUCCESS = 'DISCARD_ADJUSTMENT_REVERSAL_SUCCESS';
export const DISCARD_ADJUSTMENT_REVERSAL_FAILURE = 'DISCARD_ADJUSTMENT_REVERSAL_FAILURE';

export const SAVE_ADJUSTMENT_META_REQUEST = 'SAVE_ADJUSTMENT_META_REQUEST';
export const SAVE_ADJUSTMENT_META_SUCCESS = 'SAVE_ADJUSTMENT_META_SUCCESS';
export const SAVE_ADJUSTMENT_META_FAILURE = 'SAVE_ADJUSTMENT_META_FAILURE';

export const ADJUSTMENTS_PAGE_SIZE = 20;
export const adjustmentSchema = new schema.Entity(
  'adjustments',
  {},
  {
    idAttribute: '_id',
    processStrategy(adjustment) {
      return {
        ...adjustment,
        calculatedUIStatus: getCalculatedAdjustmentStatus(adjustment),
        lineItems: calculateLineItemStatus(adjustment),
      };
    },
  },
);
export const adjustmentsPerLineItemSchema = new schema.Entity(
  'adjustments',
  {},
  {
    idAttribute: 'lineItemId',
    processStrategy(adjustment) {
      return {
        ...adjustment,
        calculatedUIStatus: getCalculatedAdjustmentStatus(adjustment, true),
      };
    },
  },
);
export const adjustmentForReviewSchema = new schema.Entity(
  'adjustmentsForReview',
  {},
  { idAttribute: '_id' },
);

/**
 * Fetch adjustments.
 *
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const fetchAdjustments = ({
  start = 0,
  count = ADJUSTMENTS_PAGE_SIZE,
  query,
  status,
  contractId,
  duration,
  perLineItem,
}) => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/search/adjustments?${buildQueryString({
      start,
      count,
      q: query,
      status,
      contractId,
      duration,
      perLineItem,
    })}`,
    method: 'GET',
    types: [
      { type: FETCH_ADJUSTMENTS_REQUEST },
      {
        type: FETCH_ADJUSTMENTS_SUCCESS,
        payload: (action, state, res) =>
          handleResponse(
            [perLineItem ? adjustmentsPerLineItemSchema : adjustmentSchema],
            action,
            state,
            res,
          ),
        meta: (action, state, res) => ({
          totalCount: parseInt(res.headers.get('Total-Count'), 10),
        }),
      },
      { type: FETCH_ADJUSTMENTS_FAILURE, payload: handleError },
    ],
  },
});

/**
 * Create a new adjustment in Draft Status
 * @param {object} values - Object containing adjustment form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const createAdjustment = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/save`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: CREATE_ADJUSTMENT_REQUEST },
      {
        type: CREATE_ADJUSTMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      {
        type: CREATE_ADJUSTMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Save an existing adjustment
 * @param {object} values - Object containing adjustment form values
 * @param {string} id
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const editAdjustment = (values, id) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${id}/save`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: EDIT_ADJUSTMENT_REQUEST },
      {
        type: EDIT_ADJUSTMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      {
        type: EDIT_ADJUSTMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Submit a new adjustment in ApprovalRequested Status
 * @param {object} values - Object containing adjustment form values
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const submitAdjustment = values =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/submit`,
    method: 'POST',
    body: JSON.stringify(values),
    types: [
      { type: SUBMIT_ADJUSTMENT_REQUEST },
      {
        type: SUBMIT_ADJUSTMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      {
        type: SUBMIT_ADJUSTMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

/**
 * Submit an existing adjustment in ApprovalRequested Status
 * @param {object} values - Object containing adjustment form values
 * @param {string} id
 * @return {object} - Redux api middleware action that triggers & handles the request.
 */
export const submitExistingAdjustment = (values, id) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${id}/submit`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: SUBMIT_EXISTING_ADJUSTMENT_REQUEST },
      {
        type: SUBMIT_EXISTING_ADJUSTMENT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      {
        type: SUBMIT_EXISTING_ADJUSTMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

export const approveAdjustment = ({ adjustmentId }) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/approve`,
    method: 'PATCH',
    types: [
      { type: APPROVE_ADJUSTMENT_REQUEST },
      {
        type: APPROVE_ADJUSTMENT_SUCCESS,
        meta: (action, state, res) => ({ resMessage: res.message }),
      },
      {
        type: APPROVE_ADJUSTMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

export const rejectAdjustment = ({ adjustmentId, values }) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/reject`,
    method: 'PATCH',
    body: JSON.stringify(values),
    types: [
      { type: REJECT_ADJUSTMENT_REQUEST },
      {
        type: REJECT_ADJUSTMENT_SUCCESS,
        meta: (action, state, res) => ({ resMessage: res.message }),
      },
      {
        type: REJECT_ADJUSTMENT_FAILURE,
        payload: handleError,
      },
    ],
  });

export const fetchAdjustmentDetails = adjustmentId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}`,
    method: 'GET',
    types: [
      { type: FETCH_ADJUSTMENTDETAILS_REQUEST },
      {
        type: FETCH_ADJUSTMENTDETAILS_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      { type: FETCH_ADJUSTMENTDETAILS_FAILURE, payload: handleError },
    ],
  },
});

function convertFileToRequestBody(file) {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('documentType', file.type);

  return formData;
}

export const uploadAdjustmentAttachment = files => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/adjustments/attachments`,
    method: 'POST',
    body: convertFileToRequestBody(files[0]),
    options: {
      isFileUpload: true,
    },
    types: [
      { type: UPLOAD_ADJUSTMENT_ATTACHMENT_REQUEST },
      {
        type: UPLOAD_ADJUSTMENT_ATTACHMENT_SUCCESS,
      },
      { type: UPLOAD_ADJUSTMENT_ATTACHMENT_FAILURE, payload: handleError },
    ],
  },
});

export const fetchAdjustmentsForReview = () => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/tasks?areas=adjustments`,
    method: 'GET',
    types: [
      { type: FETCH_ADJUSTMENTSFORREVIEW_REQUEST },
      {
        type: FETCH_ADJUSTMENTSFORREVIEW_SUCCESS,
        payload: (action, state, res) =>
          handleResponse([adjustmentForReviewSchema], action, state, res),
      },
      { type: FETCH_ADJUSTMENTSFORREVIEW_FAILURE, payload: handleError },
    ],
  },
});

export const deleteAdjustment = adjustmentId => {
  return createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}`,
    method: 'DELETE',
    types: [
      { type: DELETE_ADJUSTMENT_REQUEST },
      {
        type: DELETE_ADJUSTMENT_SUCCESS,
        meta: (action, state, res) => ({ resMessage: res.message }),
      },
      { type: DELETE_ADJUSTMENT_FAILURE, payload: handleError },
    ],
  });
};

/**
 * Reverse an adjustments line items
 */
export const saveReversalAdjustmentDraft = (reversalRequest, adjustmentId) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/reversal/save`,
    method: 'PATCH',
    body: JSON.stringify(reversalRequest),
    types: [
      { type: REVERSE_ADJUSTMENT_DRAFT_REQUEST },
      {
        type: REVERSE_ADJUSTMENT_DRAFT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      { type: REVERSE_ADJUSTMENT_DRAFT_FAILURE, payload: handleError },
    ],
  });

/**
 * Submit a reversal request
 */
export const submitReversalAdjustment = (reversalRequest, adjustmentId) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/reversal/submit`,
    method: 'POST',
    body: JSON.stringify(reversalRequest),
    types: [
      { type: REVERSE_ADJUSTMENT_SUBMIT_REQUEST },
      {
        type: REVERSE_ADJUSTMENT_SUBMIT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      { type: REVERSE_ADJUSTMENT_SUBMIT_FAILURE, payload: handleError },
    ],
  });

/**
 * Discard reversal request
 */
export const discardAdjustmentReversal = adjustmentId =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/reversal/save`,
    method: 'PATCH',
    body: JSON.stringify({
      // sending empty line items will remove all the "draft" reversal line items
      // and therefor discard any reversals
      lineItems: [],
    }),
    types: [
      { type: DISCARD_ADJUSTMENT_REVERSAL_REQUEST },
      {
        type: DISCARD_ADJUSTMENT_REVERSAL_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      { type: DISCARD_ADJUSTMENT_REVERSAL_FAILURE, payload: handleError },
    ],
  });

/**
 * Approve a reversal request
 */
export const approveReversalAdjustment = adjustmentId =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/reversal/approve`,
    method: 'PATCH',
    types: [
      { type: REVERSE_ADJUSTMENT_APPROVE_REQUEST },
      {
        type: REVERSE_ADJUSTMENT_APPROVE_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      { type: REVERSE_ADJUSTMENT_APPROVE_FAILURE, payload: handleError },
    ],
  });

/**
 * Reject a reversal request
 */
export const rejectReversalAdjustment = (adjustmentId, rejectionBody) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/reversal/reject`,
    method: 'PATCH',
    body: JSON.stringify(rejectionBody),
    types: [
      { type: REVERSE_ADJUSTMENT_REJECT_REQUEST },
      {
        type: REVERSE_ADJUSTMENT_REJECT_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
      },
      { type: REVERSE_ADJUSTMENT_REJECT_FAILURE, payload: handleError },
    ],
  });

export const activitySchema = new schema.Entity('activity', {}, { idAttribute: '_id' });

export const fetchAdjustmentActivity = adjustmentId => ({
  [RSAA]: {
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}/activity`,
    method: 'GET',
    types: [
      { type: FETCH_ADJUSTMENTACTIVITY_REQUEST },
      {
        type: FETCH_ADJUSTMENTACTIVITY_SUCCESS,
        payload: (action, state, res) => handleResponse([activitySchema], action, state, res),
        meta: { adjustmentId },
      },
      {
        type: FETCH_ADJUSTMENTACTIVITY_FAILURE,
        payload: handleError,
      },
    ],
  },
});

/**
 * Saves non-essential adjustment fields such as comment
 * @param adjustmentId
 * @param data
 * @returns {{"[RSAA]": Object}}
 */
export const saveAdjustmentMeta = (adjustmentId, { statementComment }) =>
  createAction({
    endpoint: `${window.config.apiUrl}/adjustments/${adjustmentId}`,
    method: 'PATCH',
    body: JSON.stringify({ statementComment }),
    types: [
      { type: SAVE_ADJUSTMENT_META_REQUEST },
      {
        type: SAVE_ADJUSTMENT_META_SUCCESS,
        payload: (action, state, res) => handleResponse(adjustmentSchema, action, state, res),
        meta: { adjustmentId },
      },
      {
        type: SAVE_ADJUSTMENT_META_FAILURE,
        payload: handleError,
      },
    ],
  });
